import { Component, ElementRef, Input, OnInit, Renderer2, ViewChild, ChangeDetectorRef, OnChanges, SimpleChanges, Output, EventEmitter , NgZone } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { HideQuestionModel } from '../../../../models/hide-question/hide-question.model';
import { IncludeSurveyModel, SummaryFilterModel, SurveyCutModelCore, SurveyVersionModelCore } from '../../../../models/summary/summary.model';
import { SurveySettingSmartAnalysisModel, SurveyUserSettingModel } from '../../../../models/survey-user-setting/survey-user-setting.model';
import { DifferenceType, MasterSummaryPDModel, MasterSummaryQuestionModel, MasterSummaryQuestionOutlierModel, OutlierModel, SmartAnalysisModel, SurveyCutQuestionModel, SurveyFilterUIModel, SurveyVersionQuestionModel } from '../../../../models/survey/survey.model';
import { PercentageType } from '../../../../shared/constants/percentage.constants';
import { SortType } from '../../../../shared/constants/sort.constants';
import { calculateAbsolute, calculateRelative, getCanvasIdForAllCharts, setDefaultChart } from '../../../../shared/helpers/chart-helper';
import { CommunicationService } from '../../../../shared/services/communication.service';
import { SurveyService } from '../../surveys.service';
import * as _ from 'lodash'

@Component({
  selector: 'app-smart-analysis',
  templateUrl: './smart-analysis.component.html',
  styleUrls: ['./smart-analysis.component.less']
})
export class SmartAnalysisComponent implements OnInit, OnChanges {
  @Input() surveyId: number;
  @Input() summaryFilterModel: SummaryFilterModel;
  @Input() surveyMasterSummaryPDModelOriginal: MasterSummaryPDModel;
  @Input() includeSurveyOriginal: IncludeSurveyModel;
  @Input() listSurveyCut: SurveyCutModelCore[];
  @Input() listSurveyVersion: SurveyVersionModelCore[];
  @Input() hideQuestions: HideQuestionModel[] = [];
  @Output() smartAnalysisChanged = new EventEmitter<SurveySettingSmartAnalysisModel>();
  @Input() surveyUserSetting: SurveyUserSettingModel;

  surveyMasterSummaryPDModel: MasterSummaryPDModel;
  includeSurvey: IncludeSurveyModel;

  differenceTypes = [DifferenceType.Relative, DifferenceType.Absolute];
  differenceType: DifferenceType;

  smartAnalysisForm = this.fb.group({
    numberStandardDeviations: [1.00, Validators.compose([Validators.required, Validators.min(0.25), Validators.max(5.00)])],
    responseMin: [10, Validators.compose([Validators.required, Validators.min(0)])],
  });

  loader = false;
  outliers: OutlierModel[];
  outlierKeys: any = {};
  showPaging = false;

  selectedRow: OutlierModel;
  showChartDialog = false;

  ALL_RESPONSES = 'ALL RESPONSES';
  version: SurveyVersionModelCore = { surveyName: this.ALL_RESPONSES, hash: '', isSelected: true, missingQuestionIds: null };

  _index = -1;
  _indexChangeChartType = -1;
  dataOrdered: any;
  charContainerWidth = 0;
  charContainerHeight = 0;
  @ViewChild('allChartsContainer') allChartsContainer:ElementRef;
  limitOutlier = 500;

  constructor(
    private fb: FormBuilder, 
    private communicationService: CommunicationService,
    private renderer: Renderer2, 
    private surveyService: SurveyService,
    private cdr: ChangeDetectorRef, 
    private _ngZone: NgZone
  ) {
    this.viewChart = this.viewChart.bind(this);
  }

  ngOnInit() {
    if (this.surveyUserSetting.settingsJSON.surveySettings.smartAnalysis === null) {
        this.differenceType = DifferenceType.Relative;
    } else {
        this.smartAnalysisForm.patchValue({
            numberStandardDeviations: this.surveyUserSetting.settingsJSON.surveySettings.smartAnalysis.standardDeviation
        });
        this.smartAnalysisForm.patchValue({
            responseMin: this.surveyUserSetting.settingsJSON.surveySettings.smartAnalysis.responseMin
        });
        this.differenceType = this.surveyUserSetting.settingsJSON.surveySettings.smartAnalysis.percentageDifferenceType === PercentageType.RELATIVE ? DifferenceType.Relative : DifferenceType.Absolute;
    }

    const results = this.surveyService.getSmartAnalysisResults();
    if (results !== null) {
      if (this.includeSurvey && !this.includeSurvey.compare) {
        this.analyzeAll();
      } else {
        if (results.isAnalysisAll) {
          if (results.outliers !== undefined) {
            this.outliers = results.outliers;
            this.copyOriginalQuestions(this.surveyMasterSummaryPDModel);
            this.showPaging = true;
          } else {
            this.analyzeAll();
          }
        } else {
          this.generate();
        }
      }
    } else {
        this.generate();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['surveyMasterSummaryPDModelOriginal']) {
      if (this.surveyMasterSummaryPDModelOriginal) {
        this.surveyMasterSummaryPDModel = JSON.parse(JSON.stringify(this.surveyMasterSummaryPDModelOriginal));
      }
    }
    if(changes['includeSurveyOriginal']) {
      if (this.includeSurveyOriginal) {
        this.includeSurvey = JSON.parse(JSON.stringify(this.includeSurveyOriginal));
      }
    }
  }

  ngOnDestroy() {
    this.renderer.removeClass(document.body, 'modal-open');
  }

  changeDifferenceType(e): void {
    this.differenceType = e;
  }

  generate(): void {
    if (this.surveyMasterSummaryPDModelOriginal) {
      this.surveyMasterSummaryPDModel = JSON.parse(JSON.stringify(this.surveyMasterSummaryPDModelOriginal));
    }
    if (this.includeSurveyOriginal) {
      this.includeSurvey = JSON.parse(JSON.stringify(this.includeSurveyOriginal));
    }
    this.loader = true;
    const all = this.includeSurvey.compare ? false : true;
    setTimeout(() => {
        this.applyGenerate(all);
        this.charContainerWidth = 0;
    }, 1000);
  }

  applyGenerate(all: boolean): void {
    const differenceType = this.differenceType;
    const numberStandardDeviations = this.smartAnalysisForm.get(['numberStandardDeviations'])!.value;
    const responseMin = this.smartAnalysisForm.get(['responseMin'])!.value;
    this.outliers = [];
    this.outlierKeys = {};

    this.copyOriginalQuestions(this.surveyMasterSummaryPDModel);
    for (let i = 0; i < this.surveyMasterSummaryPDModel.listQuestionModel.length; i++) {
      setDefaultChart(this.surveyMasterSummaryPDModel.listQuestionModel[i].defaultChart.chart, this.surveyMasterSummaryPDModel.listQuestionModel[i]);
      if ((this.surveyMasterSummaryPDModel.listQuestionModel[i].answerType === 'SingleChoice' || 
            this.surveyMasterSummaryPDModel.listQuestionModel[i].answerType === 'MultipleChoice') &&
            !this.isHideQuestion(this.surveyMasterSummaryPDModel.listQuestionModel[i].id)) {

        if (this.includeSurvey.allResponsesSelected) {
          const masterChart = this.surveyMasterSummaryPDModel.listQuestionModel[i];
          masterChart.surveyName = this.ALL_RESPONSES;

          this.compileOutliersForVersionsOrCuts(new MasterSummaryQuestionOutlierModel(masterChart.surveyName, masterChart, undefined), this.surveyMasterSummaryPDModel.listSurveyVersionQuestion, null, i, false, differenceType, numberStandardDeviations);
          this.compileOutliersForVersionsOrCuts(new MasterSummaryQuestionOutlierModel(masterChart.surveyName, masterChart, undefined), this.surveyMasterSummaryPDModel.listSurveyCutQuestion, null, i, false, differenceType, numberStandardDeviations);
        }

        for (let j = 0; j < this.surveyMasterSummaryPDModel.listSurveyVersionQuestion.length; j++) {
          setDefaultChart(this.surveyMasterSummaryPDModel.listSurveyVersionQuestion[j].listQuestionModel[i].defaultChart.chart, this.surveyMasterSummaryPDModel.listSurveyVersionQuestion[j].listQuestionModel[i]);
          const masterChart = this.surveyMasterSummaryPDModel.listSurveyVersionQuestion[j].listQuestionModel[i];
          if (masterChart) {
            masterChart.surveyName = this.surveyMasterSummaryPDModel.listSurveyVersionQuestion[j].surveyName;
          }
          this.compileOutliersForVersionsOrCuts(new MasterSummaryQuestionOutlierModel(masterChart.surveyName, masterChart, undefined), this.surveyMasterSummaryPDModel.listSurveyVersionQuestion, j, i, false, differenceType, numberStandardDeviations);
          this.compileOutliersForVersionsOrCuts(new MasterSummaryQuestionOutlierModel(masterChart.surveyName, masterChart, undefined), this.surveyMasterSummaryPDModel.listSurveyCutQuestion, j, i, false, differenceType, numberStandardDeviations);
          if (this.includeSurvey.allResponsesSelected) {
            this.compileOutliersForAllResponses(new MasterSummaryQuestionOutlierModel(masterChart.surveyName, masterChart, undefined), this.surveyMasterSummaryPDModel.listQuestionModel, j, i, false, differenceType, numberStandardDeviations);
          }
        }

        for (let j = 0; j < this.surveyMasterSummaryPDModel.listSurveyCutQuestion.length; j++) {
          setDefaultChart(this.surveyMasterSummaryPDModel.listSurveyCutQuestion[j].listQuestionModel[i].defaultChart.chart, this.surveyMasterSummaryPDModel.listSurveyCutQuestion[j].listQuestionModel[i]);
          const masterChart = this.surveyMasterSummaryPDModel.listSurveyCutQuestion[j].listQuestionModel[i];
          if (masterChart) {
            masterChart.surveyName = this.surveyMasterSummaryPDModel.listSurveyCutQuestion[j].surveyName;
          }
          this.compileOutliersForVersionsOrCuts(new MasterSummaryQuestionOutlierModel(masterChart.surveyName, masterChart, this.surveyMasterSummaryPDModel.listSurveyCutQuestion[j].questionFiltersInCut), this.surveyMasterSummaryPDModel.listSurveyVersionQuestion, j, i, true, differenceType, numberStandardDeviations);
          this.compileOutliersForVersionsOrCuts(new MasterSummaryQuestionOutlierModel(masterChart.surveyName, masterChart, this.surveyMasterSummaryPDModel.listSurveyCutQuestion[j].questionFiltersInCut), this.surveyMasterSummaryPDModel.listSurveyCutQuestion, j, i, true, differenceType, numberStandardDeviations);
          if (this.includeSurvey.allResponsesSelected) {
            this.compileOutliersForAllResponses(new MasterSummaryQuestionOutlierModel(masterChart.surveyName, masterChart, this.surveyMasterSummaryPDModel.listSurveyCutQuestion[j].questionFiltersInCut), this.surveyMasterSummaryPDModel.listQuestionModel, j, i, true, differenceType, numberStandardDeviations);
          }
        }

        this.outliers.sort((a, b) => Math.abs(a.percentageDifference) > Math.abs(b.percentageDifference) ? -1 : 1);
      }
    }

    this.outliers = this.outliers.filter(m => m.removed == false);

    this.outliers = this.outliers.filter(m => m.answerPercentage > responseMin);

    if (this.outliers.length > this.limitOutlier) {
        this.outliers = this.outliers.slice(0, this.limitOutlier);
    }

    for (let i = 0; i < this.outliers.length; i++) {
        this.outliers[i].rank = i + 1;
    }

    this.showPaging = this.outliers.length < 25 ? false : true;

    let model = new SmartAnalysisModel();
    model.isAnalysisAll = all;
    model.outliers = this.outliers;
    this.surveyService.setSmartAnalysisResults(model);

    const surveySettingSmartAnalysisModel = new SurveySettingSmartAnalysisModel();
    surveySettingSmartAnalysisModel.percentageDifferenceType = differenceType;
    surveySettingSmartAnalysisModel.standardDeviation = numberStandardDeviations;
    surveySettingSmartAnalysisModel.responseMin = responseMin;
    this.smartAnalysisChanged.emit(surveySettingSmartAnalysisModel);
    this.loader = false;
  }

  analyzeAll(): void {
    var filter = JSON.parse(JSON.stringify(this.summaryFilterModel));
    filter.listQuestionFilter = [];
    filter.includeSurvey.compare = true;
    filter.includeSurvey.allResponsesSelected = true;
    filter.includeSurvey.allVersionSelected = true;
    filter.includeSurvey.listSurveyCut = this.listSurveyCut;
    filter.includeSurvey.listSurveyVersion = this.listSurveyVersion;
    this.includeSurvey.allResponsesSelected = true;
    this.includeSurvey.allVersionSelected = true;
    this.loader = true;
    this.surveyService.getMasterSummaryPDModelByQuestionFilter(this.surveyId, filter)
      .subscribe(res => {
        this.surveyMasterSummaryPDModel = res;
        this.applyGenerate(true);
      });
  }

  questionNumberValue(data: OutlierModel) {
    return '#' + (data.index + 1);
  }

  viewChart(e) {
    e.event.preventDefault();
    this.selectedRow = e.row.data;
    this.onOpenChartDialog();
    setTimeout(() => {
      const percentageType = this.differenceType === DifferenceType.Relative ? PercentageType.RELATIVE : PercentageType.ABSOLUTE;
      this.communicationService.selectedPercentage.next({
                                                          chartJ: this.selectedRow.chartJ,
                                                          questionI: this.selectedRow.questionI,
                                                          isCut: this.selectedRow.isCut,
                                                          percentageType: percentageType
                                                        });
    }, 100);
  }

  onOpenChartDialog() {
    this.showChartDialog = true;
    this.renderer.addClass(document.body, 'modal-open');
  }

  onCloseChartDialog() {
    this.showChartDialog = false;
    if (this._index != -1 && this.dataOrdered && this.dataOrdered !== SortType.RESET)  {
      this.sortModeSelected(SortType.RESET, this._index);
    }
    this.renderer.removeClass(document.body, 'modal-open');
  }

  getCanvasIdForAllCharts(questionId: number): string {
    return getCanvasIdForAllCharts(questionId);
  }

  _percentageSelected(isSelected: boolean, index: number){
    if ((this.surveyMasterSummaryPDModel.listQuestionModel[index] && this.surveyMasterSummaryPDModel.listQuestionModel[index].percentageSelected !== isSelected) || this._index !== index) {
      this.surveyMasterSummaryPDModel.listQuestionModel[index].percentageSelected = isSelected
      this.surveyMasterSummaryPDModel.listQuestionModel[index] = Object.assign({}, this.surveyMasterSummaryPDModel.listQuestionModel[index]);
    }

    if (this.surveyMasterSummaryPDModel.listSurveyVersionQuestion) {
      this.surveyMasterSummaryPDModel.listSurveyVersionQuestion.forEach(_el => {
        if ((_el.listQuestionModel[index] && _el.listQuestionModel[index].percentageSelected !== isSelected) || this._index !== index) {
         _el.listQuestionModel[index].percentageSelected = isSelected
          _el.listQuestionModel[index] = Object.assign({}, _el.listQuestionModel[index]);
        }
      })
    }

    if (this.surveyMasterSummaryPDModel.listSurveyCutQuestion) {
      this.surveyMasterSummaryPDModel.listSurveyCutQuestion.forEach(_el => {
        if ((_el.listQuestionModel[index] && _el.listQuestionModel[index].percentageSelected !== isSelected) || this._index !== index) {
          _el.listQuestionModel[index].percentageSelected = isSelected
          _el.listQuestionModel[index] = Object.assign({}, _el.listQuestionModel[index]);
        }
      })
    }
  }

  sortModeSelected(sortModeSelected: any, index: number): void {
    const _sortType = sortModeSelected._sortType
    this.dataOrdered = sortModeSelected
    if ((this.surveyMasterSummaryPDModel.listQuestionModel[index] 
        && this.surveyMasterSummaryPDModel.listQuestionModel[index].sortModeSelect !== _sortType) 
        || this._index !== index || this.surveyMasterSummaryPDModel.listQuestionModel[index].diffSortModeSelected !== sortModeSelected._baseSurvey) {
      this.surveyMasterSummaryPDModel.listQuestionModel[index].sortModeSelect = _sortType
      this.surveyMasterSummaryPDModel.listQuestionModel[index].resetPercentagesCalculus = false;
      this.surveyMasterSummaryPDModel.listQuestionModel[index].diffSortModeSelected = sortModeSelected._baseSurvey;
      this._indexChangeChartType = index
      this._index = index
    }

    if (this.surveyMasterSummaryPDModel.listSurveyVersionQuestion) {
      this.surveyMasterSummaryPDModel.listSurveyVersionQuestion.forEach(_el => {
        if ((_el.listQuestionModel[index] && _el.listQuestionModel[index].sortModeSelect !== _sortType) 
        || this._index !== index || _el.listQuestionModel[index].diffSortModeSelected !== sortModeSelected._baseSurvey) {
            _el.listQuestionModel[index].sortModeSelect = _sortType
            _el.listQuestionModel[index].resetPercentagesCalculus = false;
            _el.listQuestionModel[index].diffSortModeSelected = sortModeSelected._baseSurvey;
            this._indexChangeChartType = index
            this._index = index
          }
      });
    }

    if (this.surveyMasterSummaryPDModel.listSurveyCutQuestion) {
      this.surveyMasterSummaryPDModel.listSurveyCutQuestion.forEach(_el => {
        if ((_el.listQuestionModel[index] && _el.listQuestionModel[index].sortModeSelect !== _sortType) 
        || this._index !== index || _el.listQuestionModel[index].diffSortModeSelected !== sortModeSelected._baseSurvey) {
            _el.listQuestionModel[index].sortModeSelect = _sortType
            _el.listQuestionModel[index].resetPercentagesCalculus = false;
            _el.listQuestionModel[index].diffSortModeSelected = sortModeSelected._baseSurvey;
            this._indexChangeChartType = index
            this._index = index
          }
      });
    }
  }

  needRestartChartType(index: number): boolean {
    if(this._indexChangeChartType !== -1 && index === this._indexChangeChartType){
      this._indexChangeChartType = -1
      return false
    }else {
      return true
    }
  }

  isHideQuestion(questionId: number) {
    return this.hideQuestions.map(x => x.questionId).includes(questionId);
  }

  private compileOutliersForVersionsOrCuts(masterChart: MasterSummaryQuestionOutlierModel, listVersionOrCut: SurveyVersionQuestionModel[] | SurveyCutQuestionModel[],
            masterChartIndex: number, questionIndex: number, isCut: boolean, differenceType: DifferenceType, numberStandardDeviations: number) {
    for(let i = 0; i < listVersionOrCut.length; i++) {
      const siblingChart = listVersionOrCut[i].listQuestionModel[questionIndex];
      if (siblingChart) {
        siblingChart.surveyName = listVersionOrCut[i].surveyName;
      }
      this.compileOutliers(masterChart, new MasterSummaryQuestionOutlierModel(siblingChart.surveyName, siblingChart, (listVersionOrCut[i] as SurveyCutQuestionModel).questionFiltersInCut), masterChartIndex, questionIndex, isCut, differenceType, numberStandardDeviations);
    }
  }

  private compileOutliersForAllResponses(masterChart: MasterSummaryQuestionOutlierModel, listQuestionModel: MasterSummaryQuestionModel[],
            masterChartIndex: number, questionIndex: number, isCut: boolean, differenceType: DifferenceType, numberStandardDeviations: number) {
      const siblingChart = listQuestionModel[questionIndex];
      siblingChart.surveyName = this.ALL_RESPONSES;
      this.compileOutliers(masterChart, new MasterSummaryQuestionOutlierModel(siblingChart.surveyName, siblingChart, undefined), masterChartIndex, questionIndex, isCut, differenceType, numberStandardDeviations);
  }

  private compileOutliers(masterChart: MasterSummaryQuestionOutlierModel, siblingChart: MasterSummaryQuestionOutlierModel,
            masterChartIndex: number, questionIndex: number, isCut: boolean, differenceType: DifferenceType, 
            numberStandardDeviations: number) {
    
    if (masterChart && siblingChart && masterChart.surveyName !== siblingChart.surveyName) {
      let totalDiffPercentage = 0.0;
      let meanAverage  = 0.0;
      let standardDeviation = 0.0;
      if (masterChart.question.listAnswerModel && siblingChart.question.listAnswerModel && masterChart.question.listAnswerModel.length > 0 && siblingChart.question.listAnswerModel.length > 0) {
        // Calculate the percentage difference
        let count = 0;
        for (let i = 0; i < masterChart.question.listAnswerModel.length && i < siblingChart.question.listAnswerModel.length; i++) {
          let diffPercentage = this.calculatePercentageDifference(masterChart.question.listAnswerModel[i].answerPercentage, siblingChart.question.listAnswerModel[i].answerPercentage, differenceType);
          siblingChart.question.listAnswerModel[i].answerDiffPercShow = false;
          siblingChart.question.listAnswerModel[i].answerDiffPercentage = diffPercentage;
          if (siblingChart.question.listAnswerModel[i].answerDiffPercentage !== null) {
            totalDiffPercentage += diffPercentage;
            count++;
          }
        }
        if (count < 2) return;

        // Calculate the mean average
        meanAverage  = (totalDiffPercentage / count);
        // Calculate the standard deviation
        for (let i = 0; i < masterChart.question.listAnswerModel.length && i < siblingChart.question.listAnswerModel.length; i++) {
          if (siblingChart.question.listAnswerModel[i].answerDiffPercentage !== null) {
            standardDeviation += Math.pow(siblingChart.question.listAnswerModel[i].answerDiffPercentage - meanAverage, 2);
          }
        }
        standardDeviation = Math.sqrt(standardDeviation / (count - 1));
        let outlierMin = meanAverage - (standardDeviation * numberStandardDeviations);
        let outlierMax = meanAverage + (standardDeviation * numberStandardDeviations);
        for (let i = 0; i < masterChart.question.listAnswerModel.length && i < siblingChart.question.listAnswerModel.length; i++) {
          if (siblingChart.question.listAnswerModel[i].answerDiffPercentage !== null
              && (siblingChart.question.listAnswerModel[i].answerDiffPercentage < outlierMin || siblingChart.question.listAnswerModel[i].answerDiffPercentage > outlierMax)) {
            const outlierText = `"${siblingChart.surveyName}" is ${this.getDescriptionByPercentage(siblingChart.question.listAnswerModel[i].answerDiffPercentage)} "${masterChart.surveyName}"`;
            const outlier = this.createOutlierModel(siblingChart.question.id, siblingChart.question.questionName, siblingChart.question.listAnswerModel[i].answerName,
                                            outlierText, siblingChart.question.listAnswerModel[i].answerDiffPercentage, masterChartIndex, questionIndex, 
                                            isCut, siblingChart.question.index, siblingChart.question.listAnswerModel[i].answerPercentage, masterChart, siblingChart);

            const value = outlier.questionId + outlier.response;
            let key = siblingChart.surveyName + masterChart.surveyName + value;
            let existOutlier = this.outlierKeys[key];
            if (existOutlier === undefined) {
                key = masterChart.surveyName + siblingChart.surveyName + value;
                existOutlier = this.outlierKeys[key];
            }
            if (existOutlier === undefined) { 
                this.outlierKeys[key] = value;
                this.outliers.push(outlier);
            }
          }
        }
      } else if (masterChart.question.nizPitanja && siblingChart.question.nizPitanja && masterChart.question.nizPitanja.length > 0 && siblingChart.question.nizPitanja.length > 0) {
        // Calculate the percentage difference
        let count = 0;
        for (let i = 0; i < masterChart.question.nizPitanja.length && i < siblingChart.question.nizPitanja.length; i++) {
          let diffPercentage = this.calculatePercentageDifference(masterChart.question.nizPitanja[i].answerPercentage, siblingChart.question.nizPitanja[i].answerPercentage, differenceType);
          siblingChart.question.nizPitanja[i].answerDiffPercShow = false;
          siblingChart.question.nizPitanja[i].answerDiffPercentage = diffPercentage;
          if (siblingChart.question.nizPitanja[i].answerDiffPercentage !== null) {
            totalDiffPercentage += diffPercentage;
            count++;
          }
        }
        if (count < 2) return;

        // Calculate the mean average
        meanAverage  = (totalDiffPercentage / count);
        // Calculate the standard deviation
        for (let i = 0; i < masterChart.question.nizPitanja.length && i < siblingChart.question.nizPitanja.length; i++) {
          if (siblingChart.question.nizPitanja[i].answerDiffPercentage !== null) {
            standardDeviation += Math.pow(siblingChart.question.nizPitanja[i].answerDiffPercentage - meanAverage, 2);
          }
        }
        standardDeviation = Math.sqrt(standardDeviation / (count - 1));
        let outlierMin = meanAverage - (standardDeviation * numberStandardDeviations);
        let outlierMax = meanAverage + (standardDeviation * numberStandardDeviations);
        for (let i = 0; i < masterChart.question.nizPitanja.length && i < siblingChart.question.nizPitanja.length; i++) {
          if (siblingChart.question.nizPitanja[i].answerDiffPercentage !== null
            && (siblingChart.question.nizPitanja[i].answerDiffPercentage < outlierMin || siblingChart.question.nizPitanja[i].answerDiffPercentage > outlierMax)) {
            const outlierText = `"${siblingChart.surveyName}" is ${this.getDescriptionByPercentage(siblingChart.question.nizPitanja[i].answerDiffPercentage)} "${masterChart.surveyName}"`;
            const outlier = this.createOutlierModel(siblingChart.question.id, siblingChart.question.questionName, siblingChart.question.nizPitanja[i].answerName,
                                                outlierText, siblingChart.question.nizPitanja[i].answerDiffPercentage, masterChartIndex, questionIndex, 
                                                isCut, siblingChart.question.index, siblingChart.question.nizPitanja[i].answerPercentage, masterChart, siblingChart);

            const value = outlier.questionId + outlier.response;
            let key = siblingChart.surveyName + masterChart.surveyName + value;
            let existOutlier = this.outlierKeys[key];
            if (existOutlier === undefined) {
                key = masterChart.surveyName + siblingChart.surveyName + value;
                existOutlier = this.outlierKeys[key];
            }
            if (existOutlier === undefined) { 
                this.outlierKeys[key] = value;
                this.outliers.push(outlier);
            }
          }
        }
      }
    }
  }

  private createOutlierModel(questionId: number, question: string, response: string,
          outlierText: string, percentageDifference: number, chartJ: number, questionI: number, 
          isCut: boolean, index: number, answerPercentage: number, masterChart: MasterSummaryQuestionOutlierModel, siblingChart: MasterSummaryQuestionOutlierModel): OutlierModel {
    const outlier = new OutlierModel();

    outlier.questionId = questionId;
    outlier.questionNumber = questionI + 1;
    outlier.question = question;
    outlier.response = response;
    outlier.percentageDifference = percentageDifference;
    outlier.outlier = outlierText;
    outlier.isCut = isCut;
    outlier.index = index;
    outlier.answerPercentage = answerPercentage;
    if (masterChart.questionFiltersInCut !== undefined) {
        outlier.removed = masterChart.questionFiltersInCut.find(m => m.question.id === questionId) !== undefined;
    }
    if (siblingChart.questionFiltersInCut !== undefined) {
        outlier.removed = siblingChart.questionFiltersInCut.find(m => m.question.id === questionId) !== undefined;
    }
    if (chartJ !== null) {
      outlier.chartJ = chartJ;
      outlier.questionI = questionI;
    } else { // All responses
      outlier.chartJ = questionI;
      outlier.questionI = null;
    }
    return outlier;
  }

  private calculatePercentageDifference(masterPercentage: number, siblingPercentage: number, differenceType: DifferenceType): number {
    return DifferenceType.Absolute === differenceType
      ? calculateAbsolute(masterPercentage, siblingPercentage)
      : calculateRelative(masterPercentage, siblingPercentage);
  }

  private getDescriptionByPercentage(percentage: number): string {
    let result = "the same as";
    if (percentage > 0) {
      result = `${Math.abs(percentage)}% higher than`
    } else if(percentage < 0) {
      result = `${Math.abs(percentage)}% lower than`;
    }
    return result;
  }

  private copyOriginalQuestions(modelLoad: MasterSummaryPDModel){
    if(modelLoad){
      if(modelLoad.listQuestionModel && modelLoad.listQuestionModel.length > 0){
        let i:number;
        for(i=0;i<modelLoad.listQuestionModel.length;i++){
          modelLoad.listQuestionModel[i].copyListAnswerModel = Object.assign([],modelLoad.listQuestionModel[i].listAnswerModel);
          modelLoad.listQuestionModel[i].copyNizPitanja = Object.assign([],modelLoad.listQuestionModel[i].nizPitanja);
        }
      }
      if(modelLoad.listSurveyVersionQuestion && modelLoad.listSurveyVersionQuestion.length > 0){
        let i:number;
        for(i=0;i<modelLoad.listSurveyVersionQuestion.length;i++){
          if(modelLoad.listSurveyVersionQuestion[i] && modelLoad.listSurveyVersionQuestion[i].listQuestionModel && modelLoad.listSurveyVersionQuestion[i].listQuestionModel.length > 0){
            let j:number;
            for(j=0;j<modelLoad.listSurveyVersionQuestion[i].listQuestionModel.length;j++){
              modelLoad.listSurveyVersionQuestion[i].listQuestionModel[j].copyListAnswerModel = Object.assign([],modelLoad.listSurveyVersionQuestion[i].listQuestionModel[j].listAnswerModel);
              modelLoad.listSurveyVersionQuestion[i].listQuestionModel[j].copyNizPitanja = Object.assign([],modelLoad.listSurveyVersionQuestion[i].listQuestionModel[j].nizPitanja);
            }
          }
        }
      }
      if(modelLoad.listSurveyCutQuestion && modelLoad.listSurveyCutQuestion.length > 0){
        let i:number;
        for(i=0;i<modelLoad.listSurveyCutQuestion.length;i++){
          if(modelLoad.listSurveyCutQuestion[i] && modelLoad.listSurveyCutQuestion[i].listQuestionModel && modelLoad.listSurveyCutQuestion[i].listQuestionModel.length > 0){
            let j:number;
            for(j=0;j<modelLoad.listSurveyCutQuestion[i].listQuestionModel.length;j++){
              modelLoad.listSurveyCutQuestion[i].listQuestionModel[j].copyListAnswerModel = Object.assign([],modelLoad.listSurveyCutQuestion[i].listQuestionModel[j].listAnswerModel);
              modelLoad.listSurveyCutQuestion[i].listQuestionModel[j].copyNizPitanja = Object.assign([],modelLoad.listSurveyCutQuestion[i].listQuestionModel[j].nizPitanja);
            }
          }
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/routes/surveys/master-summary/smart-analysis/smart-analysis.component.ts